import React, { useRef, useState } from "react";
import styled, { css } from "styled-components";
import { Button, TextField } from "@material-ui/core";
import type { DriveItem } from "@microsoft/microsoft-graph-types";

import { withFirebaseUser } from "js/react/views/Auth/FirebaseUserContext";
import { ds } from "js/core/models/dataService";
import { getStaticUrl } from "js/config";
import { UIController } from "js/editor/dialogs/UIController";
import BeautifulDialog from "js/react/components/Dialogs/BeautifulDialog";
import { Gap10, Gap30 } from "js/react/components/Gap";
import { Divider, UIPane, BlueButton } from "js/react/components/UiComponents";
import * as gDrive from "js/core/utilities/gDrive";
import * as googleAuth from "js/core/oauth/googleAuth";
import * as oneDriveAuth from "js/core/oauth/oneDriveAuth";
import * as boxAuth from "js/core/oauth/boxAuth";
import ErrorPane from "js/react/components/ErrorPane";
import { ASSET_FILETYPE } from "common/constants";
import OneDriveFilePickerDialog from "js/react/components/Dialogs/OneDriveFilePickerDialog";

import MediaImporterMenuItem, { TextStyle } from "./MediaImporterMenuItem";
import { GooglePicker, BoxPicker, BoxErrorPicker, BynderPicker } from "./Pickers";
import { FILE_TYPES, mediaLibraryPaneOptions, MEDIA_TYPES, SizePaneProps } from "./PaneOptions";

import { handleBoxSelect, handleBynderFileSelected, handleDropboxClick, handleFileSelectChange, handleGoogleFileSelected, handleOneDriveFileSelected } from "../importVideoHelpers";
import { ShowDialog, ShowUpgradeDialog } from "js/react/components/Dialogs/BaseDialog";
import { UpgradePlanDialogType } from "../../MarketingDialogs/UpgradePlanDialog";
import { GoogleButton } from "../../Auth/AuthUi";

const StyledBeautifulDialog = styled(BeautifulDialog)`
  &&& {
    .dialog-paper {
      padding: 40px 50px;
      z-index: 500;
      height: 600px;
    }
  }
`;

const StyledHeader = styled.span`
    font-weight: 600;
    font-size: 23px;
    line-height: 120%;
`;

const StyledSubHeader = styled.span`
    font-weight: 400;
    font-size: 16px;
    line-height: 125%;
`;

const StyledContent = styled.div`
    display:flex;
    height: 400px;
`;

const SidePane = styled.div`
    width: 225px;
    height: 100%;
    overflow: hidden;
    display: flex;
    flex-shrink: 0;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    pointer-events: auto;
`;

const WarningText = styled.span`
    font-weight: 400;
    font-size: 12px;
    line-height: 150%;
    text-align: right;
    color: #A9A9A9;
    width: 100%;
`;

const CloudUploadIcon = styled.i`
    color: #11a9e2;
    font-size: 50px;
`;

const ExternalSourceIcon = styled.img`
    width: 50px;
`;

const hoverEffect = css`
    cursor: pointer;
    background: #f1f1f1;
`;

const StyledUIPane = styled(UIPane) <any>`
  background: transparent;
  width: auto;
  border: ${({ $solidFrame }) => $solidFrame ? "solid 1px #ccc" : "dashed 1px #11a9e2"};
  align-items: center;
  justify-content: center;
  padding: 0 50px;
  ${TextStyle}

  :hover{
    ${({ $noHover }) => $noHover ? "" : hoverEffect}
  }
`;

const StyledIconUpgrade = styled.div`
    display: flex;
    gap:20px;
    align-content: center;
`;

const Styledlabel = styled.label`
    text-align:center;
`;

interface MediaDialogProps {
    title: string
    subTitle?: string
    uploadFile: (file: any) => any
    onConfirm: () => void
    asset: any
    closeDialog: () => void
    getVideoAssetDebounced: (hostedVideoUrl: any, videoAssetId?: any) => Promise<void>
    allowedFileTypes: Array<FILE_TYPES>;
    allowedSourceTypes: Array<MEDIA_TYPES>
    warningText?: string
    firebaseUser: any
    className: any
}

const MediaDialog = ({
    title,
    subTitle,
    uploadFile,
    onConfirm,
    asset,
    closeDialog,
    getVideoAssetDebounced,
    allowedFileTypes,
    allowedSourceTypes,
    warningText,
    firebaseUser,
    className
}: MediaDialogProps) => {
    const workspaceId = UIController.getWorkspaceId();
    const uploadRef = useRef(null);
    const [url, setUrl] = useState<string>("");
    const [error, setError] = useState<string>("");

    const [selected, setSelected] = useState<MEDIA_TYPES>(MEDIA_TYPES.UPLOAD);

    const sidePaneOptions: SizePaneProps[] = allowedSourceTypes.map(sourceType =>
        mediaLibraryPaneOptions.find(option => option.id === sourceType)).filter(item => item !== undefined);

    const handleChange = value => {
        setSelected(value);
        setUrl("");
        setError("");
    };

    const renderSelectedPane = (selected: MEDIA_TYPES) => {
        switch (selected) {
            case MEDIA_TYPES.UPLOAD:
                const accept = [];
                if (allowedFileTypes.includes(FILE_TYPES.VIDEO)) {
                    accept.push("video/*");
                }
                if (allowedFileTypes.includes(FILE_TYPES.IMAGE)) {
                    accept.push("image/*");
                }
                return (<StyledUIPane className={className} onClick={() => handleMediaOptionClick(selected)}
                    onDragEnter={evt => evt.preventDefault()}
                    onDragOver={evt => evt.preventDefault()}
                    onDragLeave={evt => evt.preventDefault()}
                    onDrop={evt => handleMediaOptionClick(selected, evt, uploadFile)}>

                    <input
                        ref={uploadRef}
                        type="file"
                        style={{ display: "none" }}
                        accept={accept.join(",")}
                        onChange={e => handleFileSelectChange(e, uploadFile)}
                    />
                    <CloudUploadIcon className="logo micon">cloud_upload</CloudUploadIcon>
                    <Gap30 />
                    <label>Drag and drop, paste from clipboard, or <span className="text-blue"> browse files...</span></label>
                </StyledUIPane>);
            case MEDIA_TYPES.DROPBOX:
                const extensios = [];
                if (allowedFileTypes.includes(FILE_TYPES.IMAGE)) {
                    extensios.push(...[ASSET_FILETYPE.JPG, ASSET_FILETYPE.JPEG, ASSET_FILETYPE.PNG, ASSET_FILETYPE.SVG, ASSET_FILETYPE.GIF].map(type => `.${type}`));
                }
                if (allowedFileTypes.includes(FILE_TYPES.VIDEO)) {
                    extensios.push(...[ASSET_FILETYPE.AVI, ASSET_FILETYPE.MKV, ASSET_FILETYPE.MOV, ASSET_FILETYPE.MP4, ASSET_FILETYPE.WEBM, ASSET_FILETYPE.WMV].map(type => `.${type}`));
                }

                return (<StyledUIPane className={className} $noHover $solidFrame>
                    <ExternalSourceIcon className="logo no-scale" src={getStaticUrl("/images/file_hosting/dropbox.svg")} />
                    <Gap30 />
                    <BlueButton onClick={e => handleDropboxClick(e, uploadFile, extensios)}>Import from Dropbox</BlueButton>
                </StyledUIPane>);
            case MEDIA_TYPES.VIMEO:
            case MEDIA_TYPES.YOUTUBE:
                return (<StyledUIPane className={className} $noHover $solidFrame>
                    <TextField
                        type="url"
                        inputProps={{
                            pattern: "https://.*"
                        }}
                        required
                        placeholder="Paste the url of a Vimeo or YouTube video..."
                        value={url}
                        fullWidth
                        autoFocus
                        variant="outlined"
                        onChange={event => {
                            setUrl(event.target.value);
                            setError(event.target.validationMessage);

                            // If there are no validation errors, request the asset
                            if (!error) {
                                getVideoAssetDebounced(event.target.value);
                            }
                        }}
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                    {
                        error &&
                        <ErrorPane>{error}</ErrorPane>
                    }
                    <Gap30 />
                    <Button color="primary" variant="contained" disabled={!asset?.url} onClick={() => onConfirm()}>Add Video</Button>
                </StyledUIPane >
                );
            case MEDIA_TYPES.BYNDER:
                let bynderExtensions: FILE_TYPES[] = [];
                if (allowedFileTypes.includes(FILE_TYPES.IMAGE)) {
                    bynderExtensions.push(FILE_TYPES.IMAGE);
                }
                if (allowedFileTypes.includes(FILE_TYPES.VIDEO)) {
                    bynderExtensions.push(FILE_TYPES.VIDEO);
                }
                return (<StyledUIPane className={className} $solidFrame $noHover onClick={async () => {
                    BynderPicker((assets, additionalInfo) => {
                        handleBynderFileSelected({
                            url: assets[0].__typename === "Video" ? assets[0].previewUrls[0] : additionalInfo.selectedFile.url,
                            name: (assets as unknown)?.[0]?.name ?? ""
                        }, uploadFile);
                    }, bynderExtensions);
                }}>
                    <ExternalSourceIcon className="logo no-scale" src={getStaticUrl("/images/file_hosting/bynder-icon.svg")} />
                    <Gap30 />
                    <BlueButton>Import from Bynder</BlueButton>
                </StyledUIPane>);
            case MEDIA_TYPES.GOOGLE_DRIVE:
                return (<StyledUIPane className={className} $solidFrame $noHover>
                    <ExternalSourceIcon className="logo no-scale" src={getStaticUrl("/images/file_hosting/google-drive-icon.svg")} />
                    <Gap30 />
                    <GoogleButton
                        fullWidth={false}
                        label="Import from Google"
                        onClick={async () => {
                            let googleExtensions: FILE_TYPES[] = [];
                            if (allowedFileTypes.includes(FILE_TYPES.IMAGE)) {
                                googleExtensions.push(FILE_TYPES.IMAGE);
                            }
                            if (allowedFileTypes.includes(FILE_TYPES.VIDEO)) {
                                googleExtensions.push(FILE_TYPES.VIDEO);
                            }

                            const { accessToken } = await googleAuth.grantOfflineAccessWithDialogIfNeeded(gDrive.SCOPES.join(" "), firebaseUser.uid);
                            const picker = GooglePicker(accessToken, async value => {
                                if (value.action === "picked") {
                                    const { id, name, url } = value.docs[0];
                                    await handleGoogleFileSelected({ accessToken, id, name, url }, uploadFile);
                                }
                            }, googleExtensions);
                            picker.setVisible(true);

                            const pickerElems = document.querySelectorAll(".picker");
                            pickerElems.forEach(e => {
                                // @ts-ignore
                                e.style.zIndex = 20000;
                            });
                        }} />

                </StyledUIPane>);
            case MEDIA_TYPES.BOX:
                return (<StyledUIPane className={className} $solidFrame $noHover>
                    <ExternalSourceIcon className="logo no-scale" src={getStaticUrl("/images/file_hosting/box.svg")} />
                    <Gap30 />
                    <BlueButton onClick={async () => {
                        try {
                            let boxExtensions = [];
                            if (allowedFileTypes.includes(FILE_TYPES.IMAGE)) {
                                boxExtensions.push(...[ASSET_FILETYPE.PNG, ASSET_FILETYPE.SVG, ASSET_FILETYPE.GIF, ASSET_FILETYPE.JPG, ASSET_FILETYPE.JPEG]);
                            }
                            if (allowedFileTypes.includes(FILE_TYPES.VIDEO)) {
                                boxExtensions.push(...[ASSET_FILETYPE.MP4, ASSET_FILETYPE.MOV, ASSET_FILETYPE.WMV, ASSET_FILETYPE.AVI, ASSET_FILETYPE.MKV, ASSET_FILETYPE.WEBM]);
                            }

                            const { accessToken } = await boxAuth.grantOfflineAccess();
                            BoxPicker(accessToken, ({ id, name }) => handleBoxSelect({ id, name, accessToken }, uploadFile), boxExtensions);
                        } catch (err) {
                            BoxErrorPicker(err);
                        }
                    }}>Import from Box</BlueButton>
                </StyledUIPane>);
            case MEDIA_TYPES.UPGRADE:
                return (<StyledUIPane className={className} $solidFrame $noHover>
                    <StyledIconUpgrade>
                        <ExternalSourceIcon className="logo no-scale" src={getStaticUrl("/images/file_hosting/bynder-icon.svg")} />
                        <ExternalSourceIcon className="logo no-scale" src={getStaticUrl("/images/file_hosting/google-drive-icon.svg")} />
                        <ExternalSourceIcon className="logo no-scale" src={getStaticUrl("/images/file_hosting/box.svg")} />
                    </StyledIconUpgrade>
                    <Gap30 />
                    <Styledlabel>Upgrade to our Team Plan to import assets directly from your preferred asset management app.</Styledlabel>
                    <Gap30 />
                    <BlueButton onClick={(e: any) => {
                        ShowUpgradeDialog({
                            type: UpgradePlanDialogType.UPGRADE_PLAN_TO_TEAM,
                            analytics: { cta: "AssetIntegrations" },
                            workspaceId
                        });
                    }}>Get Started</BlueButton>
                </StyledUIPane>);
            case MEDIA_TYPES.ONE_DRIVE:
                return (<StyledUIPane className={className} $solidFrame $noHover onClick={async () => {
                    const oneDrivefileExtensions = [];
                    if (allowedFileTypes.includes(FILE_TYPES.IMAGE)) {
                        oneDrivefileExtensions.push(...[ASSET_FILETYPE.JPG, ASSET_FILETYPE.JPEG, ASSET_FILETYPE.PNG, ASSET_FILETYPE.SVG, ASSET_FILETYPE.GIF]);
                    }
                    if (allowedFileTypes.includes(FILE_TYPES.VIDEO)) {
                        oneDrivefileExtensions.push(...[ASSET_FILETYPE.AVI, ASSET_FILETYPE.MKV, ASSET_FILETYPE.MOV, ASSET_FILETYPE.MP4, ASSET_FILETYPE.WEBM, ASSET_FILETYPE.WMV]);
                    }

                    const { accessToken: token } = await oneDriveAuth.grantOfflineAccess();
                    ShowDialog(OneDriveFilePickerDialog, {
                        token, handleSelectedFile: async (selectedOneDriveFile: DriveItem) => {
                            const url = selectedOneDriveFile["@microsoft.graph.downloadUrl"];
                            handleOneDriveFileSelected({ url, name: selectedOneDriveFile.name }, uploadFile);
                        }, fileExtensions: oneDrivefileExtensions
                    });
                }}>
                    <ExternalSourceIcon className="logo no-scale" src={getStaticUrl("/images/file_hosting/onedrive.svg")} />
                    <Gap30 />
                    <BlueButton>Import from One Drive</BlueButton>
                </StyledUIPane >);
        }
    };

    const handleMediaOptionClick = (type, event = null, uploadFile = null) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        if (type == "upload") {
            if (event) {
                handleFileSelectChange(event, uploadFile);
            } else {
                uploadRef.current.click();
            }
        }
    };

    return (
        <StyledBeautifulDialog closeDialog={closeDialog} maxWidth="md" fullWidth={true} closeButton>
            <div>
                <StyledHeader>{title}</StyledHeader>
                <Gap10 />
                {subTitle && <StyledSubHeader>{subTitle}</StyledSubHeader>}
            </div>
            <Gap30 />
            <StyledContent>
                <SidePane>
                    {sidePaneOptions.map(({ icon, id, title, type }, index) => {
                        let upgradeAnalytics = {};

                        if (id === MEDIA_TYPES.UPGRADE) {
                            upgradeAnalytics = {
                                cta: "AssetIntegrations",
                                ...ds.selection?.presentation?.getAnalytics()
                            };
                        }

                        return type === "divider"
                            ? <Divider key={index} margin={10} />
                            : (
                                <MediaImporterMenuItem
                                    key={index}
                                    icon={icon}
                                    onClick={() => handleChange(id)}
                                    id={id}
                                    selected={selected}
                                    title={title}
                                    workspaceId={workspaceId}
                                    proBadgeAnalytics={upgradeAnalytics}
                                    updatePlanBadge={id === MEDIA_TYPES.UPGRADE}
                                />
                            );
                    })}
                </SidePane>
                <Gap30 />
                {renderSelectedPane(selected)}
            </StyledContent>
            <Gap10 />
            {warningText && <WarningText>{warningText}</WarningText>}
        </StyledBeautifulDialog>
    );
};

export default withFirebaseUser(MediaDialog);
